import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Volume2, VolumeX, Play, Trophy, ShieldCheck, Crown, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { Loader } from "@/components/common";
import * as spin from "@/lib/spinAudio";

const ROW_H = 66;
const REEL_VISIBLE = 5;
const REEL_CENTER = 2;
const SPIN_ROWS = 150;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function LiveDraw() {
  const { id } = useParams();
  const { user } = useAuth();
  const [drop, setDrop] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | countdown | spinning | revealed
  const [muted, setMuted] = useState(spin.isMuted());
  const [count, setCount] = useState(60);
  const [winners, setWinners] = useState([]);
  const [celebrate, setCelebrate] = useState(null);
  const [busy, setBusy] = useState(false);

  const reelRef = useRef(null);
  const rafRef = useRef(null);
  const startedRef = useRef(false);
  const countdownRef = useRef(null);
  const entrantsRef = useRef([]);
  const stripRef = useRef([]);
  const targetsRef = useRef([]);

  const load = useCallback(() => {
    api.get(`/drops/${id}`).then(({ data }) => { setDrop(data); setWinners(data.winners || []); }).catch(() => setDrop(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (drop && drop.status === "drawn") setPhase((p) => (p === "idle" ? "revealed" : p)); }, [drop]);
  useEffect(() => () => { cancelAnimationFrame(rafRef.current); clearInterval(countdownRef.current); spin.disposeSpinAudio(); }, []);

  const toggleMute = () => { const m = !muted; setMuted(m); spin.setMuted(m); };

  const buildEntrants = (wins) => {
    let entrants;
    if (drop.is_referral) {
      const names = wins.map((w) => w.name);
      entrants = names.concat(["Trainer", "Collector", "Ace", "Champion", "Fan"]).map((n) => ({ label: n, seat: null, name: n }));
    } else {
      entrants = (drop.seats || []).filter((s) => s.status === "claimed")
        .map((s) => ({ label: `${s.holder || "Trainer"} · Seat ${s.n}`, seat: s.n, name: s.holder }));
    }
    if (!entrants.length) entrants = [{ label: "Entry", seat: null, name: "Entry" }];
    return entrants;
  };

  const winnerLabel = (w) => (drop.is_referral || w.seat == null) ? (w.name || "Trainer") : `${w.name || "Trainer"} · Seat ${w.seat}`;

  const setReelTranslateY = (value) => { if (reelRef.current) reelRef.current.style.transform = `translateY(${value}px)`; };

  const animate = (from, to, duration) => new Promise((resolve) => {
    const start = performance.now();
    let lastRow = null;
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const value = from + (to - from) * easeOutCubic(t);
      setReelTranslateY(value);
      const row = Math.round(-value / ROW_H);
      if (row !== lastRow) { spin.playTick(); lastRow = row; }
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else resolve();
    };
    rafRef.current = requestAnimationFrame(step);
  });

  const fireConfetti = () => {
    const end = Date.now() + 1600;
    const colors = ["#e6b84a", "#f5c85c", "#ffffff"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const runSpin = async (wins) => {
    const targets = targetsRef.current;
    let from = 0;
    for (let r = 0; r < wins.length; r++) {
      const targetRow = targets[r];
      const to = -((targetRow - REEL_CENTER) * ROW_H);
      const duration = r === 0 ? 15000 : 10000;
      await animate(from, to, duration);
      from = to;
      if (r < wins.length - 1) { setCelebrate(wins[r]); await sleep(2400); setCelebrate(null); }
    }
    spin.stopMusic();
    spin.playChime();
    setPhase("revealed");
    fireConfetti();
  };

  const beginCountdown = (wins, { replay = false } = {}) => {
    startedRef.current = true;
    spin.primeSpinAudio();
    const entrants = buildEntrants(wins);
    entrantsRef.current = entrants;
    const N = entrants.length;
    const maxRow = SPIN_ROWS * wins.length + N + REEL_VISIBLE + 4;
    const strip = Array.from({ length: maxRow }, (_, i) => entrants[i % N].label);
    // Pin each RANDOM.org winner onto its exact landing row so the reel is guaranteed
    // to stop on the true winner (authoritative from the backend), never a stale match.
    const targets = [];
    for (let r = 0; r < wins.length; r++) {
      const targetRow = SPIN_ROWS * (r + 1);
      strip[targetRow] = winnerLabel(wins[r]);
      targets.push(targetRow);
    }
    stripRef.current = strip;
    targetsRef.current = targets;
    setWinners(wins);
    setReelTranslateY(0);
    spin.startMusic();
    if (replay) {
      setPhase("spinning");
      setTimeout(() => runSpin(wins), 40);
      return;
    }
    setPhase("countdown");
    setCount(60);
    let c = 60;
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      c -= 1;
      setCount(c);
      if (c <= 0) {
        clearInterval(countdownRef.current);
        setPhase("spinning");
        setTimeout(() => runSpin(wins), 40);
      }
    }, 1000);
  };

  const watchReplay = async () => {
    spin.primeSpinAudio();
    startedRef.current = false;
    if (winners.length) { beginCountdown(winners, { replay: true }); return; }
    try {
      const r = await api.get(`/drops/${id}/verify`);
      beginCountdown(r.data.winners, { replay: true });
    } catch (err) { toast.error(apiError(err)); }
  };

  const startDraw = async () => {
    spin.primeSpinAudio(); // user gesture unlocks audio
    setBusy(true);
    try {
      const r = drop.status === "drawn"
        ? await api.get(`/drops/${id}/verify`)
        : await api.post(`/drops/${id}/draw`);
      beginCountdown(r.data.winners);
    } catch (err) { toast.error(apiError(err)); setBusy(false); startedRef.current = false; }
  };

  useLiveSocket(useCallback((evt) => {
    if (evt.type === "draw" && String(evt.drop_id) === String(id) && drop && !startedRef.current) {
      beginCountdown(evt.winners);
    }
  }, [id, drop])); // eslint-disable-line react-hooks/exhaustive-deps

  if (drop === null) return <Loader />;
  if (drop === false) return <div className="text-center py-24 text-slate-400">Drop not found.</div>;

  const isAdmin = user?.role === "admin";
  const canStart = (drop.status === "drawing" || drop.status === "full") && isAdmin;
  const spinning = phase === "countdown" || phase === "spinning";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="text-center">
        <Link to={`/drops/${id}`} className="text-slate-400 text-sm hover:text-gold">← Back to drop</Link>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-3" data-testid="draw-title">{drop.title}</h1>
        <p className="text-slate-400 mt-1">Live draw · {drop.num_winners} winner{drop.num_winners > 1 ? "s" : ""}</p>
      </div>

      <div className="flex justify-center mt-6">
        <button onClick={toggleMute} className="flex items-center gap-2 card-surface rounded-full px-4 py-2 text-sm text-slate-200 hover:border-gold/40" data-testid="mute-toggle">
          {muted ? <VolumeX className="w-4 h-4 text-slate-400" /> : <Volume2 className="w-4 h-4 text-gold" />}
          {muted ? "Muted" : "Sound on"}
        </button>
      </div>

      {phase === "countdown" && (
        <div className="text-center mt-6">
          <div className="font-mono text-7xl font-black gold-text" data-testid="draw-countdown">{count}</div>
          <p className="text-slate-400 text-sm uppercase tracking-widest">Get ready — the reel rolls in {count}s</p>
        </div>
      )}
      {phase === "spinning" && (
        <div className="text-center mt-6">
          <div className="font-mono text-4xl font-black gold-text">🎰</div>
          <p className="text-slate-400 text-sm uppercase tracking-widest">Rolling…</p>
        </div>
      )}

      {spinning && (
        <div className="mt-8 relative mx-auto max-w-sm">
          <div className="relative rounded-2xl overflow-hidden card-surface gold-glow-lg" style={{ height: ROW_H * REEL_VISIBLE }} data-testid="draw-reel">
            <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-obsidian to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-obsidian to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-x-2 z-20 pointer-events-none border-y-2 border-gold rounded-lg gold-glow" style={{ top: REEL_CENTER * ROW_H, height: ROW_H }} />
            <div ref={reelRef} className="will-change-transform" style={{ transform: "translateY(0px)" }}>
              {stripRef.current.map((label, i) => (
                <div key={i} className="flex items-center justify-center font-display font-black text-base sm:text-xl text-white px-3 text-center truncate" style={{ height: ROW_H }}>{label}</div>
              ))}
            </div>
          </div>
          {phase === "countdown" && <p className="text-center text-xs text-slate-500 mt-3">The reel is locked until the timer hits zero…</p>}
          {celebrate && <p className="text-center text-gold font-display font-bold mt-3" data-testid="celebrate">🎉 {celebrate.name}{celebrate.seat ? ` · Seat ${celebrate.seat}` : ""}!</p>}
        </div>
      )}

      {phase === "idle" && (
        <div className="mt-8 relative mx-auto max-w-sm">
          <div className="relative rounded-2xl overflow-hidden card-surface flex flex-col items-center justify-center gap-3 text-center px-6" style={{ height: ROW_H * REEL_VISIBLE }} data-testid="draw-locked">
            <Lock className="w-10 h-10 text-gold/50" />
            <p className="text-slate-400 text-sm">{canStart ? "Ready when you are — hit Spin to begin the 60s countdown." : "The reel stays hidden until the host spins it. It'll appear here live the moment it starts."}</p>
          </div>
        </div>
      )}

      {phase === "revealed" && winners.length > 0 && (
        <div className="mt-8 animate-rise" data-testid="winners-reveal">
          <h2 className="font-display text-2xl font-black text-center gold-text flex items-center justify-center gap-2"><Crown className="w-6 h-6 text-gold" /> {winners.length > 1 ? "Winners!" : "Winner!"}</h2>
          <div className="mt-4 space-y-3">
            {winners.map((w, i) => (
              <div key={i} className="card-surface rounded-xl p-4 flex items-center justify-between gold-glow" data-testid={`winner-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center text-obsidian font-bold">{w.name?.[0]?.toUpperCase()}</div>
                  <div><p className="font-bold text-white">{w.name}</p>{w.seat && <p className="text-xs font-mono text-gold">Seat #{w.seat}</p>}</div>
                </div>
                <Trophy className="w-6 h-6 text-gold" />
              </div>
            ))}
          </div>
          <button onClick={watchReplay} className="mt-5 w-full text-slate-400 text-sm hover:text-gold flex items-center justify-center gap-2" data-testid="replay-draw-button"><Play className="w-4 h-4" /> Watch the spin again</button>
        </div>
      )}

      {phase === "idle" && (
        <div className="mt-8 text-center">
          {canStart ? (
            <button onClick={startDraw} disabled={busy} className="gold-gradient text-obsidian font-bold px-8 py-4 rounded-xl inline-flex items-center gap-2 gold-glow" data-testid="start-draw-button">
              <Play className="w-5 h-5" /> Spin
            </button>
          ) : (
            <p className="text-slate-400" data-testid="draw-waiting">The host will start the draw once seats are full. This page updates live.</p>
          )}
        </div>
      )}
    </div>
  );
}
